/**
 * RightsAttachModal — attach a licence URL / model-release doc to a
 * rendered creative. Required (business process) for anything flagged
 * "AI-generated" or "Licensed stock" before shipping to SocialPulse.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, FileCheck2, Loader2, Link as LinkIcon } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RightsAttachModal({ open, onClose, postId, size, origin, current, onSaved }) {
  const [licenseType, setLicenseType] = useState(current?.license_type || "");
  const [licenseUrl, setLicenseUrl] = useState(current?.license_url || "");
  const [releaseDocUrl, setReleaseDocUrl] = useState(current?.release_doc_url || "");
  const [releasedBy, setReleasedBy] = useState(current?.released_by || "");
  const [notes, setNotes] = useState(current?.notes || "");
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const save = async () => {
    if (!licenseUrl && !releaseDocUrl) {
      toast.error("Attach at least a licence URL or release doc URL");
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem("wt_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { data } = await axios.post(`${API}/visual-studio/rights/attach`, {
        post_id: postId, size, license_url: licenseUrl || null,
        release_doc_url: releaseDocUrl || null,
        license_type: licenseType || null,
        released_by: releasedBy || null,
        notes: notes || null,
      }, { headers });
      toast.success(`Rights attached to ${data.affected_sizes.length} render(s)`);
      onSaved?.(data.rights);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  return (
    <div
      data-testid="rights-attach-modal"
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[80] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileCheck2 size={18} className="text-brand" />
            <h3 className="font-bold text-slate-900">Attach image rights</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="rights-modal-close">
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3 text-sm">
          <p className="text-slate-500">
            This render is flagged <strong className="text-slate-700">{origin === "generated" ? "AI-generated" : origin === "licensed" ? "Licensed stock" : "Original"}</strong>.
            Attach the licence URL or release doc so the paper trail flows into the SocialPulse handoff receipt.
          </p>

          <label className="block">
            <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">License type</span>
            <input
              data-testid="rights-license-type"
              value={licenseType}
              onChange={(e) => setLicenseType(e.target.value)}
              placeholder="e.g., Unsplash Free License · Model Release · CC BY 4.0"
              className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg outline-none focus:border-brand"
            />
          </label>

          <label className="block">
            <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">License URL</span>
            <div className="relative mt-1">
              <LinkIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="rights-license-url"
                value={licenseUrl}
                onChange={(e) => setLicenseUrl(e.target.value)}
                placeholder="https://unsplash.com/license"
                className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg outline-none focus:border-brand"
              />
            </div>
          </label>

          <label className="block">
            <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Model release / rights doc URL</span>
            <div className="relative mt-1">
              <LinkIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="rights-release-doc-url"
                value={releaseDocUrl}
                onChange={(e) => setReleaseDocUrl(e.target.value)}
                placeholder="https://drive.google.com/..."
                className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg outline-none focus:border-brand"
              />
            </div>
          </label>

          <label className="block">
            <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Released by</span>
            <input
              data-testid="rights-released-by"
              value={releasedBy}
              onChange={(e) => setReleasedBy(e.target.value)}
              placeholder="Photographer / subject name"
              className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg outline-none focus:border-brand"
            />
          </label>

          <label className="block">
            <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Notes</span>
            <textarea
              data-testid="rights-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything the ad network needs to know"
              className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg outline-none focus:border-brand h-20 resize-none"
            />
          </label>
        </div>

        <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900"
          >Cancel</button>
          <button
            data-testid="rights-save-btn"
            onClick={save}
            disabled={saving}
            className="btn-primary rounded-lg px-4 py-2 text-sm font-semibold flex items-center gap-2 disabled:opacity-60"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <FileCheck2 size={14} />}
            Save rights
          </button>
        </div>
      </div>
    </div>
  );
}
